/**
 * ArColumnMenu Component
 *
 * Unified dropdown menu for column headers.
 * Combines filter typeahead with column actions (sort, etc).
 * Triggered by both left-click and right-click, aligned to the column header.
 *
 * Invariant: `setMenuOpen` is the only writer of `open`. The dropdown is
 * controlled, so antd fires `onOpenChange` only for transitions it starts —
 * Enter and right-click go through the same function or they silently skip the
 * search-value reset and the focus scheduling that ride along with it.
 *
 * The filter input and its Clear are disabled while `isDispatchingWorkflowRun` is
 * set — a column filter narrows every exported document, and the per-recipient
 * documents a workflow run is building read the live filters one at a time, so a
 * filter typed mid-run empties the documents built after it (SYD-2410). Only the
 * filter goes dead: the menu still opens, and the sort and column actions beside
 * it do not touch the row scope. `ArTableHeader` renders this menu only
 * off-portal, so PDF export mode never sees the lock.
 *
 * The menu never grows wider than the viewport less a margin each side. In card
 * mode (below `md`) it opens centred under its chip: antd shifts only a centred
 * placement along the x-axis, while `bottomLeft` can only flip, and a chip in
 * the middle of a phone screen has no room on either side to flip into.
 */

import { FC, useState, useCallback, useContext, useRef, CSSProperties } from 'react';
import { Dropdown, Input, Typography, theme, Button, type InputRef } from 'antd';
import { FilterOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import { formStore } from '@/store';
import type { TypeId } from '#types/System.Artifacts.types';

const { Text } = Typography;

type ArColumnMenuProps = {
  /** Column type ID */
  typeId: TypeId;
  /** Column long description to display at top of menu */
  longDescription?: string;
  /** Readable formula preview for formula columns */
  formulaPreview?: string;
  /** Column action menu items (sort, etc) */
  actionItems: MenuProps['items'];
  /** Whether the column is disabled */
  disabled?: boolean;
  /** Style for the trigger element */
  style?: CSSProperties;
  /** aria-label for the trigger element */
  ariaLabel?: string;
  /** Children to render inside the trigger */
  children: React.ReactNode;
  cardMode?: boolean;
};

/**
 * ArColumnMenu renders a unified dropdown menu for column headers.
 * Opens on both click and right-click, always aligned to the element.
 */
const ArColumnMenu: FC<ArColumnMenuProps> = ({
  typeId,
  longDescription,
  formulaPreview,
  actionItems,
  disabled = false,
  style,
  ariaLabel,
  children,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const uiState = useContext(ArTableUIStateContext)!;
  const filteringSnap = useSnapshot(uiState.filtering);
  const { isDispatchingWorkflowRun } = useSnapshot(formStore.state);
  const currentFilter = filteringSnap.byColumnId[typeId] ?? '';
  const [searchValue, setSearchValue] = useState('');
  const [open, setOpen] = useState(false);
  const filterInputRef = useRef<InputRef | null>(null);
  const menuOpenRef = useRef(false);

  const scheduleFocusFilterInput = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!menuOpenRef.current) return;
        filterInputRef.current?.focus({ preventScroll: true });
      });
    });
  }, []);

  /**
   * The one way this menu opens or closes. antd only calls `onOpenChange` for
   * transitions it initiates, so a bare `setOpen` from Enter or right-click skips
   * the search-value reset and the focus scheduling, and the next open shows the
   * previous keystrokes.
   */
  const setMenuOpen = useCallback(
    (visible: boolean) => {
      menuOpenRef.current = visible;
      setOpen(visible);
      if (!visible) {
        setSearchValue('');
        return;
      }
      scheduleFocusFilterInput();
    },
    [scheduleFocusFilterInput]
  );

  /**
   * The one writer of this column's filter, and where the workflow-run lock is
   * enforced rather than merely shown: the disabled input is the affordance, this
   * guard is the invariant (SYD-2410).
   */
  const handleFilterChange = useCallback(
    (value: string) => {
      if (isDispatchingWorkflowRun) return;
      const next = value.trim();
      if (next.length === 0) {
        delete uiState.filtering.byColumnId[typeId];
      } else {
        uiState.filtering.byColumnId[typeId] = next;
      }
    },
    [isDispatchingWorkflowRun, typeId, uiState.filtering.byColumnId]
  );

  const handleClearFilter = useCallback(() => {
    delete uiState.filtering.byColumnId[typeId];
    setSearchValue('');
  }, [typeId, uiState.filtering.byColumnId]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(true);
    },
    [disabled, setMenuOpen]
  );

  const hasFilter = currentFilter.length > 0;
  const viewportCap = `calc(100vw - ${token.margin * 2}px)`;

  const filterMenuItems: MenuProps['items'] = [
    ...(longDescription
      ? [
        {
          key: 'long-description',
          type: 'group' as const,
          label: (
            <Text italic type="secondary" style={{ whiteSpace: 'wrap' }}>
              {longDescription}
            </Text>
          ),
        },
      ]
      : []),
    ...(formulaPreview
      ? [
        {
          key: 'formula-preview',
          type: 'group' as const,
          label: (
            <Text code style={{ whiteSpace: 'pre-wrap' }}>
              ƒ {formulaPreview}
            </Text>
          ),
        },
      ]
      : []),
    {
      key: 'filter-input',
      label: (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            ref={filterInputRef}
            aria-label={`artable-filter-input-${typeId}`}
            placeholder={isDispatchingWorkflowRun
              ? 'Locked while a workflow run builds its documents'
              : 'Type to filter...'}
            disabled={isDispatchingWorkflowRun}
            prefix={<FilterOutlined style={{ color: token.colorTextSecondary }} />}
            suffix={
              searchValue ? (
                <CloseCircleOutlined
                  onClick={() => setSearchValue('')}
                  style={{ color: token.colorTextSecondary, cursor: 'pointer' }}
                />
              ) : null
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onPressEnter={() => {
              handleFilterChange(searchValue);
              setMenuOpen(false);
            }}
            size="small"
            style={{ width: '100%' }}
          />
        </div>
      ),
      disabled: true,
    },
    ...(hasFilter
      ? [
        { type: 'divider' as const, key: 'filter-divider-clear' },
        {
          key: 'clear-filter',
          label: (
            <Button
              type="link"
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={handleClearFilter}
              disabled={isDispatchingWorkflowRun}
              style={{ padding: `${token.paddingXS}px 0` }}
            >
              Clear filter: {currentFilter}
            </Button>
          ),
        },
      ]
      : []),
  ];


  const menuItems: MenuProps['items'] = [
    ...filterMenuItems,
    { type: 'divider' as const, key: 'filter-actions-divider' },
    ...(actionItems ?? []),
  ];

  return (
    <Dropdown
      menu={{ 
        items: menuItems, 
        style: {
          minWidth: `min(300px, ${viewportCap})`,
          maxWidth: `min(500px, ${viewportCap})`,
        },
      }}
      trigger={['click']}
      placement={cardMode ? 'bottom' : 'bottomLeft'}
      disabled={disabled}
      open={open}
      onOpenChange={setMenuOpen}
    >
      <div
        role="columnheader"
        aria-label={ariaLabel}
        style={style}
        onContextMenu={handleContextMenu}
      >
        {children}
      </div>
    </Dropdown>
  );
};

export default ArColumnMenu;
